<div class="om-page">
  <app-project-nav></app-project-nav>
  <app-page-header
    [title]="'objectMappings.review.title' | translate"
    [subtitle]="'objectMappings.review.subtitle' | translate"
  />

  @if (resultText(); as done) {
    <p class="cr-result" role="status" data-testid="om-review-result">
      {{ done.key | translate: done.params }}
    </p>
  }

  @if (state() === 'loading') {
    <div class="om-loading" data-testid="om-review-loading">
      <mat-spinner diameter="32" />
    </div>
  } @else if (state() === 'error') {
    <div class="om-hint om-hint--error" role="alert" data-testid="om-review-error">
      <p>{{ 'objectMappings.review.loadError' | translate }}</p>
      <button mat-stroked-button type="button" data-testid="om-review-retry" (click)="retry()">
        {{ 'objectMappings.retry' | translate }}
      </button>
    </div>
  } @else if (rows().length === 0) {
    <div class="om-hint" data-testid="om-review-empty">
      <p>{{ 'objectMappings.review.empty' | translate }}</p>
      <a mat-stroked-button data-testid="om-review-library-link" [routerLink]="libraryLink">
        {{ 'objectMappings.review.toLibrary' | translate }}
      </a>
    </div>
  } @else {
    @if (positionHintKey(); as hintKey) {
      <p class="cr-banner" role="note" data-testid="om-review-position-hint">
        <mat-icon aria-hidden="true">warning</mat-icon>
        {{ hintKey | translate }}
      </p>
    }

    <section class="om-card cr-card" [attr.aria-label]="'objectMappings.review.listLabel' | translate">
      <div class="om-table-header cr-card__head">
        <span data-testid="om-review-count">
          {{ countKey() | translate: { total: rows().length, selected: selectedCount() } }}
        </span>
        @if (!isProjectReader()) {
          <button type="button" class="cr-link" data-testid="om-review-select-all" (click)="toggleAll()">
            {{ (allSelected() ? 'objectMappings.review.deselectAll' : 'objectMappings.review.selectAll') | translate }}
          </button>
        }
      </div>
      <ul class="cr-list">
        @for (row of rows(); track row.id) {
          <li class="cr-row" [class.cr-row--off]="!row.selected" data-testid="om-review-row">
            <div class="cr-row__main">
              @if (!isProjectReader()) {
                <mat-checkbox
                  data-testid="om-review-select"
                  [checked]="row.selected"
                  [aria-label]="'objectMappings.review.select' | translate: { name: row.name }"
                  (change)="toggleSelected(row.id)"
                />
              }
              @if (row.showImage && imageUrl(row.id); as src) {
                <img class="cr-image" data-testid="om-review-image" alt="" [src]="src" />
              } @else {
                <span
                  class="cr-image cr-image--empty"
                  data-testid="om-review-image-placeholder"
                  [attr.aria-label]="'objectMappings.review.noImage' | translate"
                >
                  <mat-icon aria-hidden="true">image</mat-icon>
                </span>
              }
              <span class="cr-name">
                @if (isProjectReader()) {
                  <span class="om-name" data-testid="om-review-name-text">{{ row.name }}</span>
                } @else {
                  <input
                    [id]="nameFieldId(row.id)"
                    class="cr-name__input"
                    data-testid="om-review-name"
                    autocomplete="off"
                    [class.cr-name__input--invalid]="row.selected && row.issue === 'invalid'"
                    [value]="row.name"
                    [attr.maxlength]="fieldLengths.NAME_MAX"
                    [attr.aria-label]="'objectMappings.review.nameLabel' | translate"
                    [attr.aria-invalid]="row.issue === 'invalid'"
                    (input)="onNameInput(row.id, $event)"
                  />
                }
              </span>
              <span class="cr-row__pills">
                @if (row.capture.typeName) {
                  <span class="om-pill" data-testid="om-review-type">{{ row.capture.typeName }}</span>
                }
                @if (row.pill; as pill) {
                  <app-status-pill
                    data-testid="om-review-pill"
                    size="sm"
                    [kind]="pill.kind"
                    [label]="'objectMappings.review.pill.' + pill.key | translate: { name: pill.name }"
                  />
                }
              </span>
              <span class="cr-row__count" data-testid="om-review-candidate-count">
                @if (row.candidates.length === 1) {
                  {{ 'objectMappings.review.candidateCountOne' | translate }}
                } @else {
                  {{ 'objectMappings.review.candidateCount' | translate: { count: row.candidates.length } }}
                }
              </span>
              @if (row.candidates.length > 0) {
                <button
                  type="button"
                  class="om-toggle"
                  data-testid="om-review-expand"
                  [attr.aria-expanded]="isExpanded(row.id)"
                  [attr.aria-controls]="'cr-candidates-' + row.id"
                  [attr.aria-label]="
                    (isExpanded(row.id) ? 'objectMappings.review.collapse' : 'objectMappings.review.expand') | translate
                  "
                  (click)="toggleExpanded(row.id)"
                >
                  <mat-icon aria-hidden="true">{{ isExpanded(row.id) ? 'expand_less' : 'expand_more' }}</mat-icon>
                </button>
              }
            </div>

            @if (row.selected && row.issue === 'invalid' && !isProjectReader()) {
              <p class="cr-note cr-note--error" data-testid="om-review-name-error">
                {{ 'objectMappings.rename.invalidName' | translate }}
              </p>
            }
            @if (row.selected && row.issue && row.issue !== 'invalid' && !isProjectReader()) {
              <div class="cr-note cr-note--warn">
                <span [attr.data-testid]="'om-review-' + row.issue">
                  {{ 'objectMappings.review.' + row.issue | translate }}
                </span>
                <span class="cr-note__actions">
                  @if (row.issue !== 'duplicate') {
                    <button
                      mat-stroked-button
                      type="button"
                      data-testid="om-review-replace"
                      [attr.aria-pressed]="row.replace"
                      [class.cr-chosen]="row.replace"
                      (click)="replace(row.id)"
                    >
                      {{ 'objectMappings.review.replace' | translate }}
                    </button>
                  }
                  <button mat-stroked-button type="button" data-testid="om-review-rename" (click)="rename(row.id)">
                    {{ 'objectMappings.review.rename' | translate }}
                  </button>
                </span>
              </div>
            } @else if (row.capture.duplicateOf; as other) {
              <div class="cr-note cr-note--info">
                <span data-testid="om-review-same-element">
                  {{ 'objectMappings.review.sameElement' | translate: { name: other } }}
                </span>
                @if (row.selected && !isProjectReader()) {
                  <button mat-stroked-button type="button" data-testid="om-review-discard" (click)="discard(row.id)">
                    {{ 'objectMappings.review.discard' | translate }}
                  </button>
                }
              </div>
            }

            @if (isExpanded(row.id)) {
              <div class="cr-ladder">
                <ol
                  class="cr-ladder__list"
                  [id]="'cr-candidates-' + row.id"
                  [attr.aria-label]="'objectMappings.review.candidatesLabel' | translate"
                >
                  @for (candidate of row.candidates; track candidate.index; let i = $index) {
                    <li class="cr-candidate" [class.cr-candidate--first]="i === 0" data-testid="om-review-candidate">
                      <span class="cr-candidate__index">{{ i + 1 }}</span>
                      <span class="om-strategy">{{ candidate.locator.strategy }}</span>
                      <code class="om-locator">{{ candidate.locator.value }}</code>
                      @if (!isProjectReader()) {
                        <button
                          type="button"
                          class="cr-candidate__remove"
                          data-testid="om-review-candidate-remove"
                          [disabled]="row.candidates.length <= 1"
                          [attr.aria-label]="'objectMappings.review.removeCandidate' | translate: { index: i + 1 }"
                          (click)="removeCandidate(row, candidate.index)"
                        >
                          <mat-icon aria-hidden="true">close</mat-icon>
                        </button>
                      }
                    </li>
                  }
                </ol>
                <p class="cr-ladder__hint">{{ 'objectMappings.review.ladderHint' | translate }}</p>
              </div>
            }
          </li>
        }
      </ul>

      @if (!isProjectReader()) {
        <div class="cr-footer">
          <p class="cr-footer__summary" data-testid="om-review-summary">
            {{ 'objectMappings.review.summary' | translate: summary() }}
          </p>
          @if (acceptError(); as failure) {
            <p class="cr-footer__error" role="alert" data-testid="om-review-accept-error">
              {{ failure.message ?? ('objectMappings.review.acceptError' | translate) }}
            </p>
          }
          <button
            mat-flat-button
            type="button"
            data-testid="om-review-accept"
            [disabled]="!canAccept()"
            (click)="accept()"
          >
            {{ 'objectMappings.review.accept' | translate }}
          </button>
        </div>
      }
    </section>
  }
</div>
